import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import ResumeBox from "@/common/components/ResumeBox";
import { ShowHeader } from "@/common/components/ShowHeader";
import Skeleton from "@/common/components/Skeleton";
import { useGoBack } from "@/common/hooks/useGoBack";
import { formatCurrency } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useClientCompanyPaymentMethods } from "../../hooks/useClientCompanyPaymentMethods";
import { ClientPaymentMethodData } from "../../types/paymentMethods.type";
import { useCreateBankingDeposit } from "../../hooks/useCreateBankingDeposit";
import { bankingDepositCreateSchema, BankingDepositCreateValidationSchema } from "../../schemas/create.schema";
import useCompanyRemainingCreditAndBalance from "@/common/hooks/queries/useCompanyRemainingCreditAndBalance";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";

const BankingDepositsCreate = () => {
  const goBack = useGoBack();
  const navigate = useNavigate();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { data: company } = useMineCompany();

  const {
    data: companyRemainingCreditAndBalance,
    isPending: isCompanyRemainingCreditAndBalancePending,
  } = useCompanyRemainingCreditAndBalance();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const hasUserPermissionToGetCompanyRemainingCreditAndBalance = hasUserPermission("Client::Companies::RemainingCreditAndBalance");

  const {
    data: paymentMethods,
    isPending: isPaymentMethodsPending,
    dataUpdatedAt: paymentMethodsUpdatedAt,
  } = useClientCompanyPaymentMethods<ClientPaymentMethodData[]>({});

  const {
    mutate: createBankingDeposit,
    isPending: isCreateBankingDepositPending,
    isError: isCreateBankingDepositError,
  } = useCreateBankingDeposit({
    onSuccess: (response) => {
      const bankingDepositId = response.data?.data?.id;

      if (bankingDepositId) {
        navigate(`/banking-deposits/${bankingDepositId}`);
      } else {
        goBack({
          fallback: `/banking-deposits`,
          searchLastRouteFromFallback: true,
        });
      }
    },
    onError: (error) => {
      setShowConfirmationModal(false);
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<BankingDepositCreateValidationSchema>({
    resolver: zodResolver(bankingDepositCreateSchema),
  });

  const onSubmit: SubmitHandler<BankingDepositCreateValidationSchema> = (data) => createBankingDeposit(data);

  useEffect(() => {
    if (!watch('payment_method') && paymentMethods && paymentMethods[0]) {
      setValue('payment_method', {
        id: paymentMethods[0].id,
        display_name: paymentMethods[0].display_name,
      });
    }
  }, [paymentMethodsUpdatedAt]);

  return (
    <ErrorMessageWrapper
      statusCode={company?.kind === "public_company" ? 404 : 200}
      notFoundHideButton
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          backTo="/banking-deposits"
          titleClassName="font-normal text-md text-white-400"
          title="Depósitos"
          subtitle="Adicionar saldo"
        />

        <Box className="flex flex-col gap-6 shadow-lg max-h-[calc(100vh-128px)] overflow-auto">
          <div className="flex flex-col gap-4 ">
            <p className="text-lg font-bold">
              Insira o valor
            </p>

            <div className="space-y-3">
              <Controller
                control={control}
                name="requested_total"
                render={({ field }) => (
                  <CurrencyTextField
                    label="Valor aportado"
                    markAsRequired
                    {...field}
                    errorMessage={errors.requested_total?.message || undefined}
                    disabled={isCreateBankingDepositPending}
                  />

                )}
              />

              {(isUserPermissionsPending || hasUserPermissionToGetCompanyRemainingCreditAndBalance) && (
                <div className="flex items-center gap-3">
                  <ResumeBox
                    padding="small"
                    boxClassName="w-full flex-1"
                    label="Crédito disponível (Pós-pago)"
                    value={companyRemainingCreditAndBalance?.remaining_credit
                      ? formatCurrency(Number(companyRemainingCreditAndBalance?.remaining_credit))
                      : '--'}
                    isLoading={isCompanyRemainingCreditAndBalancePending}
                  />

                  <div className="flex-1 flex items-center gap-3">
                    <ResumeBox
                      padding="small"
                      boxClassName="w-full flex-1"
                      label="Saldo disponível (Pré-pago)"
                      value={companyRemainingCreditAndBalance?.remaining_balance
                        ? formatCurrency(Number(companyRemainingCreditAndBalance?.remaining_balance))
                        : '--'}
                      isLoading={isCompanyRemainingCreditAndBalancePending}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-lg font-bold">
              Forma de pagamento
            </p>

            <div className="flex items-center gap-6">
              {isPaymentMethodsPending && new Array(2).fill(null).map((_, index) => (
                <div key={`loading-payment-method-${index}`} className="flex items-center gap-3">
                  <Skeleton circle className="size-6 bg-white-100" />

                  <Skeleton width={Math.min(30 * (index + 1), 80)} />
                </div>
              ))}

              {!isPaymentMethodsPending && paymentMethods.map((paymentMethod, index) => (
                <Checkbox
                  name="payment_method"
                  type="radio"
                  className="items-center gap-3"
                  boxClassName="rounded-full size-6"
                  defaultChecked={index === 0}
                  disabled={isCreateBankingDepositPending}
                  labelClassName="text-base"
                  label={paymentMethod.display_name}
                  Icon={paymentMethod.icon?.url ? (
                    <img
                      src={paymentMethod.icon?.url}
                      alt={paymentMethod.display_name}
                      className="size-7"
                    />
                  ) : undefined}
                  onChange={() => setValue("payment_method", {
                    id: paymentMethod.id,
                    display_name: paymentMethod.display_name,
                  })}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="success"
              type="button"
              onClick={handleSubmit(() => setShowConfirmationModal(true))}
              disabled={isCreateBankingDepositPending}
            >
              Avançar para pagamento
            </Button>
          </div>
        </Box>

        <Modal
          isOpen={showConfirmationModal}
          setIsOpen={setShowConfirmationModal}
          title="Você tem certeza?"
          subtitle="Seu pix será gerado agora, caso deseje outro valor, faça um novo deposito."
          titleClass="text-2xl"
          subtitleClassName="mt-2 text-white-400"
          useCancellableFooter
          showClose={false}
          confirmButtonText="Confirmar Depósito"
          confirmButtonClassName="bg-primary-blue"
          confirmButtonIsLoading={isCreateBankingDepositPending}
          onConfirm={handleSubmit(onSubmit)}
          disabled={isCreateBankingDepositPending}
        />

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError={isCreateBankingDepositError}
          />
        )}
      </form>
    </ErrorMessageWrapper>
  );
}

export default BankingDepositsCreate;
